import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, errorMessage, get, post } from "../../api";
import { NotifyGrid } from "../../components/notify-grid";
import { Alert, Badge, Button, Loading, PageHeader, Panel, PanelHeader, formatDateTime } from "../../components/ui";
import { type PushState, pushState, turnOffPush, turnOnPush } from "../../lib/push";

type Device = { id: string; endpoint: string; userAgent: string; createdAt: string; lastSentAt: string | null };

/** A readable name for a device from its browser's user agent. */
function deviceName(ua: string) {
  const os = /iPhone|iPad/.test(ua) ? "iPhone or iPad" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : "Device";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "browser";
  return `${os}, ${browser}`;
}

const STATE_TEXT: Record<PushState, string> = {
  unsupported: "This browser can't get notifications.",
  "needs-install": "On iPhone and iPad, add the CRM to your home screen first (Share → Add to Home Screen), open it from there, then turn notifications on.",
  blocked: "Notifications are blocked for this site. Allow them in the browser's site settings, then come back.",
  off: "Off on this device.",
  on: "On for this device.",
};

/** My profile → Notifications: push notifications on this device (spec §5), for everyone. */
/** `embedded`: a section of My profile, under its own small heading. */
export function NotificationSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [state, setState] = useState<PushState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const devices = useQuery({ queryKey: ["push-devices"], queryFn: () => get<Device[]>("/api/push/subscriptions") });
  const refresh = () => {
    pushState().then(setState);
    qc.invalidateQueries({ queryKey: ["push-devices"] });
  };
  useEffect(() => {
    pushState().then(setState);
  }, []);
  const on = useMutation({ mutationFn: turnOnPush, onSuccess: () => (setNotice("Notifications are on for this device."), refresh()) });
  const off = useMutation({ mutationFn: turnOffPush, onSuccess: () => (setNotice(null), refresh()) });
  const test = useMutation({ mutationFn: () => post<{ sent: number }>("/api/push/test"), onSuccess: (r) => setNotice(r.sent ? `Sent to ${r.sent} device${r.sent === 1 ? "" : "s"}.` : "No devices have notifications on.") });
  const remove = useMutation({ mutationFn: (id: string) => api("DELETE", "/api/push/subscriptions", { id }), onSuccess: refresh });
  const error = on.error ?? off.error ?? test.error ?? remove.error;

  return (
    <div className="flex flex-col gap-5">
      {embedded ? (
        <div>
          <h2 className="text-lg font-semibold">Notifications</h2>
          <p className="max-w-prose text-sm text-muted">What you hear about (tasks, your jobs' proposals, payments, and replies, and more), and how: push on your phone or computer, a text, or an email.</p>
        </div>
      ) : (
        <PageHeader title="My notifications" description="What you hear about (tasks, your jobs' proposals, payments, and replies, and more), and how: push on your phone or computer, a text, or an email." />
      )}
      {error && <Alert>{errorMessage(error)}</Alert>}
      {notice && <Alert tone="ok">{notice}</Alert>}
      <Panel>
        <PanelHeader title="This device" />
        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          {state === null ? <Loading /> : <p>{STATE_TEXT[state]}</p>}
          <div className="flex flex-wrap gap-2">
            {state === "off" && (
              <Button variant="primary" busy={on.isPending} onClick={() => on.mutate()}>
                Turn on notifications
              </Button>
            )}
            {state === "on" && (
              <Button variant="secondary" busy={off.isPending} onClick={() => off.mutate()}>
                Turn off on this device
              </Button>
            )}
            <Button variant="quiet" busy={test.isPending} onClick={() => test.mutate()}>
              Send a test
            </Button>
          </div>
        </div>
      </Panel>
      <NotifyGrid url="/api/push/prefs" />
      <Panel>
        <PanelHeader title="Your devices" description="Every phone and computer where you turned notifications on." />
        {devices.isLoading ? (
          <div className="px-5">
            <Loading />
          </div>
        ) : !devices.data?.length ? (
          <p className="px-5 py-4 text-sm text-muted">None yet.</p>
        ) : (
          <ul className="text-sm">
            {devices.data.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-2 border-b border-mist px-5 py-3 last:border-0">
                <span className="font-medium">{deviceName(d.userAgent)}</span>
                <span className="text-muted">since {formatDateTime(d.createdAt)}</span>
                {d.lastSentAt && <Badge tone="ok">last sent {formatDateTime(d.lastSentAt)}</Badge>}
                <Button variant="quiet" className="ml-auto px-2 py-1" busy={remove.isPending && remove.variables === d.id} onClick={() => remove.mutate(d.id)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
